/**
 * Fiche publique Koikispass d'une date agenda.
 * Les lignes sélectionnées portent agenda_id ; les candidates portent id.
 */
export function koikispassAgendaUrl(row) {
  const direct = typeof row?.public_url === 'string' ? row.public_url.trim() : '';
  if (/^https?:\/\//i.test(direct)) {
    return direct;
  }

  const rawId = row?.agenda_id ?? row?.id;
  const id = Number(rawId);
  const slug = typeof row?.slug === 'string' ? row.slug.trim().replace(/^\/+|\/+$/g, '') : '';
  if (!Number.isInteger(id) || id <= 0 || slug === '') {
    return null;
  }

  return `https://www.koikispass.com/lagenda/${id}/${encodeURIComponent(slug)}/`;
}

export const DEFAULT_COMMERCANTS = 15;
export const DEFAULT_OFFRES = 10;
export const DEFAULT_DATES = 20;
export const DEFAULT_AGENDA_DATES = 20;
export const FUTURE_MONTHS_AHEAD = 6;

export const BUCKET_OPTIONS = [
  { value: 'all', label: 'Tous' },
  { value: 'nouveaux', label: 'Nouveaux', icon: 'mdi:new-box' },
  { value: 'non_parue', label: 'Non parue', icon: 'mdi:eye-off-outline' },
  { value: 'parue', label: 'Parue', icon: 'mdi:eye-outline' },
  { value: 'a_renouveller', label: 'À renouveler', icon: 'mdi:calendar-refresh' },
];

export const BUCKET_LABELS = {
  nouveaux: 'Nouveaux',
  non_parue: 'Non parue',
  parue: 'Parue',
  a_renouveller: 'À renouveler',
};

/** Ordre d’affichage « Tous » = onglets (nouveaux → non parue → parue → à renouveler). */
export const BUCKET_SORT_ORDER = ['nouveaux', 'non_parue', 'parue', 'a_renouveller'];

export const EMPTY_COMMERCANT_EDIT = {
  nom: '',
  activite: '',
  complement: '',
  copyright: '',
  photo_path: '',
  is_nouveau: false,
};

export const EMPTY_OFFRE_EDIT = {
  commercant_label: '',
  description: '',
  remise: '',
  prix: '',
  prix_barre: '',
  apartirde: '',
  copyright: '',
  photo_path: '',
  is_nouveau: false,
};

/** Résumé imprimé du set de table : plafond éditorial. */
export const SET_RESUME_MAX_LENGTH = 45;

export const EMPTY_SET_EDIT = {
  thematique: '',
  titre: '',
  date_label: '',
  resume: '',
  prix: '',
  infos: '',
  lieu: '',
};

export function bucketVariant(bucket) {
  if (bucket === 'nouveaux') return 'success';
  if (bucket === 'non_parue') return 'info';
  if (bucket === 'a_renouveller') return 'warning';
  return 'neutral';
}

/** Statut pastille (hors « Nouveaux », déjà en badge haut-gauche). */
export function primaryStatut(item) {
  if (item?.statut && item.statut !== 'nouveaux') return item.statut;
  const fromBuckets = (item?.buckets ?? []).find((b) => b !== 'nouveaux');
  return fromBuckets || null;
}

export function pickerBucket(item) {
  if (Boolean(item?.is_nouveau) || (item?.buckets ?? []).includes('nouveaux')) {
    return 'nouveaux';
  }
  const statut = item?.statut && item.statut !== 'nouveaux'
    ? item.statut
    : (item?.buckets ?? []).find((b) => b !== 'nouveaux');
  if (statut && BUCKET_SORT_ORDER.includes(statut)) {
    return statut;
  }
  return 'parue';
}

export function compareProposeOrder(a, b) {
  const ba = pickerBucket(a);
  const bb = pickerBucket(b);
  const oa = BUCKET_SORT_ORDER.indexOf(ba);
  const ob = BUCKET_SORT_ORDER.indexOf(bb);
  if (oa !== ob) return oa - ob;
  if (ba === 'parue') {
    const la = Number(a.last_seen ?? 0);
    const lb = Number(b.last_seen ?? 0);
    if (la !== lb) return la - lb;
  }
  const labelA = String(a.nom || a.commercant_label || '');
  const labelB = String(b.nom || b.commercant_label || '');
  return labelA.localeCompare(labelB, 'fr');
}

/** Mois métier de travail = M+1 (toujours dans l’historique). */
export function workingMonth() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Août → juillet (double page été). */
export function canonicalMonth(ym) {
  if (!ym) return ym;
  const [y, m] = ym.split('-').map(Number);
  if (m === 8) return `${y}-07`;
  return ym;
}

export function addMonthsYm(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function formatMonthLabel(ym) {
  if (!ym) return '—';
  const canon = canonicalMonth(ym);
  const [y, m] = canon.split('-').map(Number);
  if (m === 7) return `Juillet / Août ${y}`;
  const label = new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatRemise(row) {
  if (row.remise == null || Number(row.remise) <= 0) return null;
  const n = Number(row.remise);
  return Number.isInteger(n) ? `-${n}%` : `-${n}%`;
}

export function formatPrix(value) {
  if (value == null || String(value).trim() === '') return '';
  const raw = String(value).trim().replace(/\s/g, '').replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return String(value).trim();
  const n = Number(raw);
  const hasCents = Math.round(n * 100) % 100 !== 0;
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
  return `${formatted} €`;
}

/** Les nouveaux doivent tous précéder les anciens. */
export function isNouveauBeforeAncien(items) {
  let seenAncien = false;
  for (const item of items) {
    if (item.is_nouveau) {
      if (seenAncien) return false;
    } else {
      seenAncien = true;
    }
  }
  return true;
}
